import { createEffect, createSignal, Match, onCleanup, onMount, Show, Switch } from "solid-js";
import type { Session } from "@supabase/supabase-js";
import { AuthScreen } from "./components/AuthScreen";
import { TodoApp } from "./components/TodoApp";
import { BENCHMARK_USER_ID, readBenchmarkCount, seedBenchmarkData } from "./lib/benchmark";
import { createTodoStore } from "./lib/store";
import { hasSupabaseConfig, supabase } from "./lib/supabase";

export default function App() {
  const store = createTodoStore();
  const [session, setSession] = createSignal<Session | null>(null);
  const [authReady, setAuthReady] = createSignal(false);
  const [benchmarkMode, setBenchmarkMode] = createSignal(false);

  onMount(() => {
    const benchmarkCount = readBenchmarkCount();
    if (benchmarkCount) {
      setBenchmarkMode(true);
      seedBenchmarkData(benchmarkCount)
        .catch((error) => console.error("Benchmark seed failed", error))
        .finally(() => setAuthReady(true));
      return;
    }

    if (!supabase) {
      setAuthReady(true);
      void store.load(null);
      return;
    }

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setAuthReady(true);
    });

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });
    onCleanup(() => data.subscription.unsubscribe());
  });

  createEffect(() => {
    if (!authReady()) return;
    void store.load(
      benchmarkMode() ? BENCHMARK_USER_ID : session()?.user.id ?? null,
      !benchmarkMode(),
    );
  });

  return (
    <Switch>
      <Match when={benchmarkMode()}>
        <TodoApp store={store} email="Benchmark mode" />
      </Match>
      <Match when={!hasSupabaseConfig}>
        <AuthScreen offlineSetup />
      </Match>
      <Match when={!authReady()}>
        <main class="center-shell" aria-busy="true">
          <p class="eyebrow">Loading</p>
          <h1>Preparing your workspace.</h1>
        </main>
      </Match>
      <Match when={!session()}>
        <AuthScreen />
      </Match>
      <Match when={session()}>
        <Show when={session()}>
          {(current) => <TodoApp store={store} email={current().user.email ?? "Signed in"} />}
        </Show>
      </Match>
    </Switch>
  );
}
